<template>
  <section class="editor-screen">
    <div class="editor-shell">
      <div class="editor-header">
        <div class="editor-title">
          <button class="editor-back" @click="$emit('close')">返回我的层级</button>
          <span class="eyebrow">Layer Studio</span>
          <h2>编辑层级档案</h2>
          <div class="active-draft-chip">
            <span>{{ activeDraftId === null ? '当前编辑：正式档案' : '当前编辑：暂存目录' }}</span>
            <strong>{{ activeDraftDisplayName }}</strong>
          </div>
        </div>
        <div class="editor-header-actions">
          <el-button class="studio-ghost-btn" @click="$emit('patrol')">巡逻层级</el-button>
          <el-button class="studio-purple-btn" @click="handleOpenDrafts">暂存目录</el-button>
          <el-button class="studio-save-btn" :loading="draftButtonLoading" @click="handleDraftSave">暂存</el-button>
          <el-button class="studio-primary-btn" :loading="saveButtonLoading" @click="handleSave">提交审核</el-button>
        </div>
      </div>

      <div class="editor-workspace">
        <EditorStudioPreview
          :edit-form="editForm"
          :edit-preview-style="editPreviewStyle"
          :edit-cover-style="editCoverStyle"
          :edit-cover-url="editCoverUrl"
          :patrol-expire-days="patrolExpireDays"
          :preview-html="livePreviewHtml"
        />

        <aside class="editor-panel">
          <EditorStudioMedia :edit-form="editForm" :uploading-field="uploadingField" @image-file="forwardImageFile" />

          <el-form :model="editForm" label-position="top" class="edit-form studio-form">
            <section class="editor-block">
              <div class="block-head">
                <span class="eyebrow">Profile</span>
                <strong>基础信息</strong>
              </div>
              <div class="editor-help">提示：连续 {{ patrolExpireDays }} 天未巡逻，该层级会自动成为无主层级，其他用户可重新占领。</div>
              <div class="edit-grid">
                <el-form-item label="层级名称">
                  <el-input v-model="editForm.name" maxlength="20" />
                </el-form-item>
                <el-form-item label="展示标题">
                  <el-input v-model="editForm.title" maxlength="30" />
                </el-form-item>
              </div>
              <el-form-item label="简介">
                <el-input v-model="editForm.description" type="textarea" :rows="3" maxlength="120" show-word-limit />
              </el-form-item>
              <el-form-item label="层级公告">
                <el-input v-model="editForm.notice" type="textarea" :rows="2" maxlength="100" show-word-limit />
              </el-form-item>
            </section>

            <EditorStudioRichContent
              ref="richContentRef"
              :html="String(editForm.html || '')"
              @update:html="updateHtml"
              @update:preview-html="livePreviewHtml = $event"
            />
          </el-form>
        </aside>
      </div>
    </div>

    <el-dialog
      v-model="draftVisible"
      width="760px"
      class="draft-directory-dialog"
      custom-class="draft-directory-dialog"
      modal-class="draft-directory-overlay"
      append-to-body
    >
      <template #header>
        <div class="draft-dialog-title">
          <span class="eyebrow">Draft Directory</span>
          <strong>暂存目录</strong>
          <small>当前编辑：{{ activeDraftDisplayName }}</small>
        </div>
      </template>
      <div class="draft-directory-tools">
        <el-input
          v-model="draftSearchText"
          placeholder="查找目录名称"
          clearable
          @keyup.enter="handleDraftSearch"
        />
        <el-button class="studio-ghost-btn" @click="handleDraftSearch">查找</el-button>
        <el-button class="studio-primary-btn" :loading="draftButtonLoading" @click="handleDraftCreate">新增</el-button>
      </div>

      <el-table
        v-loading="draftLoading"
        :data="directoryRows"
        :row-key="getDraftRowKey"
        class="draft-directory-table"
        empty-text="暂无暂存目录"
      >
        <el-table-column label="目录" min-width="210">
          <template #default="{ row }">
            <div class="draft-directory-name">
              <strong>{{ row.directoryName }}</strong>
              <el-tag v-if="row.isDefault" size="small" type="success">默认当前</el-tag>
              <el-tag v-else-if="activeDraftId === row.id" size="small">正在编辑</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="180">
          <template #default="{ row }">
            {{ row.updateAt || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="right">
          <template #default="{ row }">
            <el-button class="draft-row-link" size="small" link @click="handleDraftSelect(row)">编辑</el-button>
            <el-button
              v-if="!row.isDefault"
              class="draft-row-link danger"
              size="small"
              link
              @click="handleDraftDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ImageField, LevelDraftItem, LevelItem } from './types';
import EditorStudioPreview from './editor-studio-preview.vue';
import EditorStudioMedia from './editor-studio-media.vue';
import EditorStudioRichContent from './editor-studio-rich-content.vue';

const props = defineProps<{
  editForm: Partial<LevelItem>;
  editSaving: boolean;
  uploadingField: '' | ImageField;
  editPreviewStyle: Record<string, string>;
  editCoverStyle: Record<string, string>;
  editCoverUrl: string;
  patrolExpireDays: number;
  draftDialogVisible: boolean;
  draftItems: LevelDraftItem[];
  draftLoading: boolean;
  draftSaving: boolean;
  activeDraftId: number | null;
  activeDraftName: string;
  currentDraftUpdateAt: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
  (e: 'draftSave'): void;
  (e: 'draftCreate'): void;
  (e: 'draftOpen'): void;
  (e: 'draftSearch', keyword: string): void;
  (e: 'draftSelect', draft: Partial<LevelDraftItem> & { isDefault?: boolean }): void;
  (e: 'draftDelete', draft: LevelDraftItem): void;
  (e: 'update:draftDialogVisible', value: boolean): void;
  (e: 'patrol'): void;
  (e: 'imageFile', field: ImageField, file: File): void;
}>();

const livePreviewHtml = ref('');
type RichContentExpose = {
  syncHtmlBeforeSave: () => void;
  isImageUploading: boolean;
};

const richContentRef = ref<RichContentExpose | null>(null);
const draftSearchText = ref('');
const editorImageUploading = computed(() => !!richContentRef.value?.isImageUploading);
const saveButtonLoading = computed(() => props.editSaving || !!props.uploadingField || editorImageUploading.value);
const draftButtonLoading = computed(() => props.draftSaving || !!props.uploadingField || editorImageUploading.value);
const activeDraftDisplayName = computed(() => {
  if (props.activeDraftId === null) return '当前正式档案';
  return props.activeDraftName || '未命名暂存目录';
});
const draftVisible = computed({
  get: () => props.draftDialogVisible,
  set: (value: boolean) => emit('update:draftDialogVisible', value)
});
const directoryRows = computed(() => [
  {
    id: null,
    directoryName: '当前',
    updateAt: props.currentDraftUpdateAt,
    isDefault: true
  },
  ...props.draftItems
]);

const handleSave = () => {
  richContentRef.value?.syncHtmlBeforeSave();
  emit('save');
};

const handleDraftSave = () => {
  richContentRef.value?.syncHtmlBeforeSave();
  emit('draftSave');
};

const handleDraftCreate = () => {
  richContentRef.value?.syncHtmlBeforeSave();
  emit('draftCreate');
};

const handleOpenDrafts = () => {
  emit('draftOpen');
};

const handleDraftSearch = () => {
  emit('draftSearch', draftSearchText.value.trim());
};

const handleDraftSelect = (draft: Partial<LevelDraftItem> & { isDefault?: boolean }) => {
  emit('draftSelect', draft);
};

const handleDraftDelete = (draft: LevelDraftItem) => {
  emit('draftDelete', draft);
};

const getDraftRowKey = (draft: Partial<LevelDraftItem> & { isDefault?: boolean }) => {
  return draft.isDefault ? 'current' : String(draft.id || '');
};

const updateHtml = (value: string) => {
  props.editForm.html = value;
};

const forwardImageFile = (field: ImageField, file: File) => {
  emit('imageFile', field, file);
};

watch(
  () => props.editForm.html,
  (html) => {
    livePreviewHtml.value = String(html || '');
  },
  { immediate: true }
);
</script>

<style lang="scss">
.editor-screen {
  width: 100%;
  height: calc(100vh - 44px);
  min-height: 720px;
  overflow: hidden;
}

.editor-shell {
  width: 100%;
  max-width: 1680px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.editor-header,
.editor-block,
.live-preview {
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.045), rgba(157, 92, 255, 0.022)),
    rgba(11, 16, 30, 0.72);
  border-radius: 8px;
  box-shadow: 0 20px 70px rgba(0, 0, 0, 0.38), 0 0 26px rgba(0, 240, 216, 0.055);
  backdrop-filter: blur(24px) saturate(125%);
}

.editor-header {
  flex: 0 0 auto;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 18px;
  padding: 20px;
  margin-bottom: 18px;
}

.editor-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  margin-bottom: 14px;
  padding: 0 16px;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.46);
  background: rgba(0, 240, 216, 0.11);
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 24px rgba(0, 240, 216, 0.12);
  transition: border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.editor-back::before {
  content: "<";
  font-weight: 700;
}

.editor-back:hover {
  border-color: rgba(0, 240, 216, 0.72);
  background: rgba(0, 240, 216, 0.16);
  box-shadow: 0 0 32px rgba(0, 240, 216, 0.18);
  transform: translateY(-1px);
}

.editor-header h2 {
  margin-top: 8px;
  font-size: clamp(28px, 4vw, 48px);
  line-height: 1;
}

.editor-title {
  min-width: 0;
}

.active-draft-chip {
  width: fit-content;
  max-width: min(520px, 100%);
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 10px 12px;
  color: var(--muted);
  border: 1px solid rgba(0, 240, 216, 0.28);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.11), rgba(157, 92, 255, 0.07)),
    rgba(4, 9, 20, 0.72);
  border-radius: 999px;
  box-shadow: 0 0 24px rgba(0, 240, 216, 0.09), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.active-draft-chip span {
  font-size: 12px;
}

.active-draft-chip strong {
  min-width: 0;
  max-width: 260px;
  overflow: hidden;
  color: var(--accent);
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 0 14px rgba(0, 240, 216, 0.22);
}

.editor-header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.editor-header-actions .el-button,
.draft-directory-tools .el-button {
  min-height: 38px;
  border-radius: 8px;
  font-weight: 700;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease, background 0.25s ease;
}

.editor-header-actions .el-button:hover,
.draft-directory-tools .el-button:hover {
  transform: translateY(-1px);
}

.studio-ghost-btn {
  --el-button-bg-color: rgba(0, 240, 216, 0.09);
  --el-button-border-color: rgba(0, 240, 216, 0.38);
  --el-button-text-color: var(--accent);
  --el-button-hover-bg-color: rgba(0, 240, 216, 0.16);
  --el-button-hover-border-color: rgba(0, 240, 216, 0.66);
  --el-button-hover-text-color: #dffffb;
  --el-button-active-bg-color: rgba(0, 240, 216, 0.2);
  --el-button-active-border-color: rgba(0, 240, 216, 0.78);
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.1);
}

.studio-purple-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
  --el-button-active-bg-color: rgba(157, 92, 255, 0.24);
  --el-button-active-border-color: rgba(157, 92, 255, 0.82);
  box-shadow: 0 0 22px rgba(157, 92, 255, 0.11);
}

.studio-save-btn {
  --el-button-bg-color: rgba(34, 184, 255, 0.13);
  --el-button-border-color: rgba(34, 184, 255, 0.44);
  --el-button-text-color: #bfefff;
  --el-button-hover-bg-color: rgba(34, 184, 255, 0.21);
  --el-button-hover-border-color: rgba(34, 184, 255, 0.72);
  --el-button-hover-text-color: #ecfbff;
  --el-button-active-bg-color: rgba(34, 184, 255, 0.26);
  --el-button-active-border-color: rgba(34, 184, 255, 0.84);
  box-shadow: 0 0 22px rgba(34, 184, 255, 0.1);
}

.studio-primary-btn.el-button {
  color: #03101a;
  border-color: rgba(0, 240, 216, 0.78);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.96), rgba(34, 184, 255, 0.9));
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.32);
}

.studio-primary-btn.el-button:hover,
.studio-primary-btn.el-button:focus {
  color: #03101a;
  border-color: rgba(207, 255, 248, 0.92);
  background:
    linear-gradient(135deg, #7ffff2, #55cfff);
  box-shadow: 0 0 34px rgba(0, 240, 216, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.studio-primary-btn.el-button.is-disabled,
.studio-primary-btn.el-button.is-loading {
  color: rgba(3, 16, 26, 0.72);
  border-color: rgba(0, 240, 216, 0.36);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.58), rgba(34, 184, 255, 0.48));
}

.editor-screen .studio-ghost-btn.el-button,
.el-overlay .draft-directory-dialog .studio-ghost-btn.el-button {
  color: var(--accent) !important;
  border-color: rgba(0, 240, 216, 0.38) !important;
  background: rgba(0, 240, 216, 0.09) !important;
  background-color: rgba(0, 240, 216, 0.09) !important;
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.1) !important;
}

.editor-screen .studio-ghost-btn.el-button:hover,
.el-overlay .draft-directory-dialog .studio-ghost-btn.el-button:hover {
  color: #dffffb !important;
  border-color: rgba(0, 240, 216, 0.66) !important;
  background: rgba(0, 240, 216, 0.16) !important;
  background-color: rgba(0, 240, 216, 0.16) !important;
}

.editor-screen .studio-purple-btn.el-button {
  color: #d4bdff !important;
  border-color: rgba(157, 92, 255, 0.42) !important;
  background: rgba(157, 92, 255, 0.12) !important;
  background-color: rgba(157, 92, 255, 0.12) !important;
  box-shadow: 0 0 22px rgba(157, 92, 255, 0.11) !important;
}

.editor-screen .studio-purple-btn.el-button:hover {
  color: #f0e8ff !important;
  border-color: rgba(157, 92, 255, 0.72) !important;
  background: rgba(157, 92, 255, 0.2) !important;
  background-color: rgba(157, 92, 255, 0.2) !important;
}

.editor-screen .studio-save-btn.el-button {
  color: #bfefff !important;
  border-color: rgba(34, 184, 255, 0.44) !important;
  background: rgba(34, 184, 255, 0.13) !important;
  background-color: rgba(34, 184, 255, 0.13) !important;
  box-shadow: 0 0 22px rgba(34, 184, 255, 0.1) !important;
}

.editor-screen .studio-save-btn.el-button:hover {
  color: #ecfbff !important;
  border-color: rgba(34, 184, 255, 0.72) !important;
  background: rgba(34, 184, 255, 0.21) !important;
  background-color: rgba(34, 184, 255, 0.21) !important;
}

.editor-screen .studio-primary-btn.el-button,
.el-overlay .draft-directory-dialog .studio-primary-btn.el-button {
  color: #03101a !important;
  border-color: rgba(0, 240, 216, 0.78) !important;
  background: linear-gradient(135deg, rgba(0, 240, 216, 0.96), rgba(34, 184, 255, 0.9)) !important;
  background-color: #00f0d8 !important;
  box-shadow: 0 0 26px rgba(0, 240, 216, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.32) !important;
}

.editor-screen .studio-primary-btn.el-button:hover,
.el-overlay .draft-directory-dialog .studio-primary-btn.el-button:hover {
  color: #03101a !important;
  border-color: rgba(207, 255, 248, 0.92) !important;
  background: linear-gradient(135deg, #7ffff2, #55cfff) !important;
  background-color: #7ffff2 !important;
}

.profile-link-btn {
  --el-button-bg-color: rgba(157, 92, 255, 0.12);
  --el-button-border-color: rgba(157, 92, 255, 0.42);
  --el-button-text-color: #d4bdff;
  --el-button-hover-bg-color: rgba(157, 92, 255, 0.2);
  --el-button-hover-border-color: rgba(157, 92, 255, 0.72);
  --el-button-hover-text-color: #f0e8ff;
}

.draft-directory-dialog {
  --text: var(--cyber-text, #e8edf7);
  --muted: var(--cyber-muted, #8992a9);
  --accent: var(--cyber-cyan, #00f0d8);
  --blue: var(--cyber-blue, #22b8ff);
  --el-dialog-bg-color: transparent;
  --el-dialog-title-font-size: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 216, 0.24);
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(0, 240, 216, 0.055), rgba(157, 92, 255, 0.035)),
    rgba(7, 12, 24, 0.96);
  box-shadow: 0 22px 80px rgba(0, 0, 0, 0.56), 0 0 34px rgba(0, 240, 216, 0.1);
  backdrop-filter: blur(24px) saturate(125%);
}

.el-overlay.draft-directory-overlay {
  background-color: rgba(2, 5, 12, 0.78) !important;
  backdrop-filter: blur(8px) saturate(120%);
}

.el-overlay .draft-directory-dialog.el-dialog,
.draft-directory-dialog.el-dialog,
.draft-directory-overlay .el-dialog {
  background:
    linear-gradient(180deg, rgba(17, 24, 39, 0.98), rgba(8, 13, 26, 0.98)),
    #080d1a !important;
  background-color: #080d1a !important;
  border: 1px solid rgba(55, 65, 81, 0.86) !important;
  box-shadow: 0 28px 110px rgba(0, 0, 0, 0.64), 0 0 42px rgba(0, 240, 216, 0.12) !important;
}

.draft-directory-dialog .el-dialog__header,
.draft-directory-overlay .el-dialog__header {
  margin-right: 0;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(45, 52, 66, 0.95) !important;
  background:
    radial-gradient(circle at 8% 0%, rgba(0, 240, 216, 0.06), transparent 36%),
    linear-gradient(90deg, rgba(17, 24, 39, 0.92), rgba(8, 13, 26, 0.92)) !important;
}

.el-overlay .draft-directory-dialog .el-dialog__header,
.el-overlay .draft-directory-dialog .el-dialog__body,
.draft-directory-overlay .el-dialog__header,
.draft-directory-overlay .el-dialog__body {
  background-color: #080d1a !important;
}

.draft-directory-dialog .el-dialog__body,
.draft-directory-overlay .el-dialog__body {
  padding: 20px 24px 24px;
  color: var(--text);
}

.draft-directory-dialog .el-dialog__headerbtn {
  top: 14px;
}

.draft-directory-dialog .el-dialog__close {
  color: var(--accent);
}

.draft-dialog-title {
  display: grid;
  gap: 6px;
}

.draft-dialog-title strong {
  color: var(--text);
  font-size: 22px;
  line-height: 1;
}

.draft-dialog-title small {
  color: var(--muted);
}

.draft-directory-tools {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 216, 0.14);
  background: rgba(4, 9, 20, 0.5);
  border-radius: 10px;
}

.draft-directory-dialog .el-input__wrapper {
  border-radius: 8px;
  background: rgba(4, 9, 20, 0.86) !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.24) inset !important;
}

.draft-directory-dialog .el-input__inner {
  color: var(--text) !important;
}

.draft-directory-dialog .el-input__inner::placeholder {
  color: rgba(137, 146, 169, 0.72);
}

.draft-directory-table {
  --el-table-bg-color: rgba(5, 10, 22, 0.92);
  --el-table-tr-bg-color: rgba(5, 10, 22, 0.92);
  --el-table-header-bg-color: rgba(8, 13, 26, 0.96);
  --el-table-border-color: rgba(45, 52, 66, 0.92);
  --el-table-border: 1px solid rgba(45, 52, 66, 0.92);
  --el-table-text-color: var(--text);
  --el-table-header-text-color: var(--muted);
  overflow: hidden;
  border: 1px solid rgba(45, 52, 66, 0.92);
  border-radius: 10px;
  background: rgba(5, 10, 22, 0.92);
}

.draft-directory-table .el-table__inner-wrapper::before,
.draft-directory-table .el-table__inner-wrapper::after,
.draft-directory-table .el-table__border-left-patch,
.draft-directory-table .el-table__border-right-patch {
  background-color: rgba(45, 52, 66, 0.95) !important;
}

.draft-directory-table .el-table__header th.el-table__cell {
  background: rgba(8, 13, 26, 0.96) !important;
  border-bottom-color: rgba(45, 52, 66, 0.95) !important;
}

.draft-directory-table .el-table__body td.el-table__cell {
  background: rgba(5, 10, 22, 0.92) !important;
  border-bottom-color: rgba(39, 48, 64, 0.78) !important;
}

.draft-directory-table .el-table__row:hover > td.el-table__cell {
  background: rgba(0, 240, 216, 0.055) !important;
}

.draft-directory-table::before {
  background-color: rgba(45, 52, 66, 0.95) !important;
}

.draft-directory-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.draft-directory-name strong {
  color: var(--text);
}

.draft-row-link.el-button {
  --el-button-text-color: var(--accent);
  --el-button-hover-text-color: #dffffb;
  font-weight: 700;
  text-shadow: 0 0 12px rgba(0, 240, 216, 0.16);
}

.draft-row-link.el-button.danger {
  --el-button-text-color: #ff9caa;
  --el-button-hover-text-color: #ffd1d8;
  text-shadow: 0 0 12px rgba(255, 92, 116, 0.18);
}

.editor-workspace {
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 440px);
  gap: 18px;
  align-items: start;
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.preview-pane,
.editor-panel {
  gap: 14px;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  padding-right: 4px;
  scrollbar-gutter: stable;
}

.preview-pane {
  display: block;
  overflow-y: auto;
}

.editor-panel {
  display: grid;
  overflow-y: auto;
}

.live-preview {
  position: relative;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-position: center;
  background-size: cover;
  scrollbar-gutter: stable;
}

.live-preview::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(rgba(0, 240, 216, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.04) 1px, transparent 1px);
  background-size: 34px 34px;
}

.preview-shade {
  display: none;
}

.preview-content {
  position: relative;
  z-index: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 36px;
}

.preview-content::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, rgba(5, 9, 20, 0.92), rgba(5, 9, 20, 0.48)),
    linear-gradient(180deg, rgba(5, 9, 20, 0.08), rgba(5, 9, 20, 0.94));
}

.preview-content > * {
  position: relative;
  z-index: 1;
}

.preview-content h2 {
  margin: 16px 0;
  font-size: clamp(36px, 5vw, 76px);
  line-height: 0.98;
}

.preview-content > p {
  max-width: 720px;
  color: var(--muted);
  line-height: 1.8;
}

.preview-cover-card {
  width: min(520px, 100%);
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: stretch;
  margin-top: 22px;
  padding: 12px;
  border: 1px solid rgba(0, 240, 216, 0.18);
  border-radius: 8px;
  background: rgba(5, 10, 22, 0.46);
  backdrop-filter: blur(16px);
}

.preview-cover-img {
  height: 150px;
  display: grid;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(0, 240, 216, 0.2);
  border-radius: 8px;
  background:
    linear-gradient(rgba(0, 240, 216, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(157, 92, 255, 0.035) 1px, transparent 1px),
    #0b101e;
  background-position: center;
  background-size: cover;
}

.preview-cover-card strong {
  display: block;
  margin-top: 6px;
  color: var(--text);
}

.preview-metrics {
  max-width: 720px;
}

.preview-html {
  max-width: 820px;
  max-height: none;
  padding-left: 22px;
  padding-right: 18px;
}

.preview-html ul,
.preview-html ol {
  padding-left: 26px;
}

.preview-html li {
  padding-left: 4px;
}

.block-head {
  margin-bottom: 16px;
}

.block-head strong {
  display: block;
  color: var(--text);
}

.editor-block {
  padding: 18px;
  min-width: 0;
}

.upload-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.upload-card {
  min-height: 118px;
  text-align: left;
  color: var(--muted);
  border: 1px dashed rgba(0, 240, 216, 0.32);
  background:
    linear-gradient(135deg, rgba(0, 240, 216, 0.06), rgba(157, 92, 255, 0.035)),
    rgba(8, 13, 26, 0.68);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
}

.upload-card:hover {
  color: var(--text);
  border-color: rgba(0, 240, 216, 0.58);
  box-shadow: 0 0 22px rgba(0, 240, 216, 0.1);
}

.upload-card span,
.upload-card small {
  display: block;
}

.upload-card strong {
  display: block;
  margin: 8px 0;
  color: var(--accent);
  font-size: 18px;
}

.file-input {
  display: none;
}

.studio-form {
  display: grid;
  gap: 14px;
}

.edit-form .el-form-item__label {
  color: var(--muted);
}

.edit-form .el-input__wrapper,
.edit-form .el-textarea__inner {
  border-radius: 8px;
  background: rgba(4, 9, 20, 0.86) !important;
  box-shadow: 0 0 0 1px rgba(0, 240, 216, 0.24) inset !important;
}

.edit-form .el-input__inner,
.edit-form .el-textarea__inner {
  color: var(--text) !important;
}

.edit-form .el-input__count,
.edit-form .el-input__count-inner {
  color: #fff !important;
  background: transparent !important;
  box-shadow: none !important;
}

.edit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.rich-editor {
  width: 100%;
  min-width: 0;
  min-height: 0;
  border: 1px solid rgba(0, 240, 216, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(8, 13, 26, 0.82);
  --w-e-toolbar-bg-color: #08111f;
  --w-e-toolbar-color: var(--text);
  --w-e-toolbar-border-color: rgba(0, 240, 216, 0.18);
  --w-e-toolbar-active-bg-color: rgba(0, 240, 216, 0.12);
  --w-e-toolbar-active-color: var(--accent);
  --w-e-textarea-bg-color: rgba(5, 10, 22, 0.92);
  --w-e-textarea-color: var(--text);
  --w-e-textarea-slight-color: var(--muted);
  --w-e-textarea-slight-bg-color: rgba(0, 240, 216, 0.08);
  --w-e-textarea-border-color: rgba(0, 240, 216, 0.34);
  --w-e-textarea-selected-border-color: var(--accent);
  --w-e-textarea-handler-bg-color: var(--accent);
  --w-e-modal-button-bg-color: rgba(0, 240, 216, 0.08);
  --w-e-modal-button-border-color: rgba(0, 240, 216, 0.28);
}

.rich-editor.w-e-full-screen-container {
  z-index: 5000 !important;
  border: 0;
  border-radius: 0;
  background: #07101d;
  box-shadow: none;
}

.rich-editor.w-e-full-screen-container .editor-body {
  height: auto !important;
  min-height: 0;
  flex: 1 !important;
}

.rich-editor.w-e-full-screen-container .w-e-text-container {
  min-height: 0;
}

.editor-toolbar {
  min-width: 0;
  border-bottom: 1px solid rgba(0, 240, 216, 0.18) !important;
}

.editor-body {
  height: 520px !important;
}

body.level-editor-fullscreen-active {
  overflow: hidden;
}

body.level-editor-fullscreen-active .editor-screen,
body.level-editor-fullscreen-active .editor-shell,
body.level-editor-fullscreen-active .editor-workspace,
body.level-editor-fullscreen-active .editor-panel,
body.level-editor-fullscreen-active .studio-form,
body.level-editor-fullscreen-active .editor-block {
  overflow: visible !important;
  filter: none !important;
  backdrop-filter: none !important;
  transform: none !important;
}

@media (max-width: 980px) {
  .editor-screen,
  .editor-shell {
    height: auto;
    overflow: visible;
  }

  .editor-workspace {
    grid-template-columns: 1fr;
  }

  .editor-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .edit-grid,
  .upload-grid,
  .draft-directory-tools {
    grid-template-columns: 1fr;
  }

  .live-preview,
  .preview-content {
    min-height: 560px;
  }

  .preview-pane,
  .editor-panel {
    height: auto;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }
}
</style>
